<template>
  <v-row no-gutters class="align-end">
    <v-col cols="3">
      <data-input
        class="my-0 py-0 ml-3 pt-5"
        :menu-props="{ offsetY: true }"
        :items="computedAttributes"
        v-model="mainAttribute"
        item-text="verbose_name"
        item-value="name"
        @change="emit('mainAttribute')"
        :rules="rules"
        :data-type="dataType"
        :label="chooseAttributeLabel"
      >
      </data-input>
    </v-col>
    <v-spacer></v-spacer>
    <v-col cols="3">
      <v-tooltip bottom color="font" :disabled="isAttributeRequiredTooltipDisabled">
        <template #activator="{ on }">
          <span v-on="on">
            <data-input
              :disabled="!mainAttribute"
              class="my-0 py-0 ml-3 pt-5"
              :menu-props="{ offsetY: true }"
              :items="computedExpressions"
              v-model="expression"
              item-text="verbose_name"
              item-value="name"
              @change="emit"
              :rules="rules"
              :data-type="dataType"
              :label="chooseExpressionLabel"
            >
            </data-input>
          </span>
        </template>
        <span>
          {{ attributeRequiredTooltip }}
        </span>
      </v-tooltip>
    </v-col>
    <v-spacer></v-spacer>
    <v-col cols="3">
      <v-row v-if="!isExpressionSpecial" no-gutters class="justify-center">
        <v-row>
          <v-col cols="12">
            <v-tooltip bottom color="font" :disabled="isAttributeRequiredTooltipDisabled">
              <template #activator="{ on }">
                <span v-on="on">
                  <v-radio-group
                    :disabled="!mainAttribute"
                    class="my-0 py-0 input--type"
                    v-model="attributeValueType"
                    @change="emit"
                    :rules="rules"
                    dense
                    row
                    hide-details
                  >
                    <v-radio :label="$i18n.t('dialog.value')" value="value"></v-radio>
                    <v-radio :label="$i18n.t('dialog.attribute')" value="attribute"></v-radio>
                  </v-radio-group>
                </span>
              </template>
              <span>
                {{ attributeRequiredTooltip }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row no-gutters style="width: 100%">
          <v-col>
            <v-tooltip bottom color="font" :disabled="isValueTooltipDisabled">
              <template #activator="{ on }">
                <span v-on="on">
                  <data-input
                    :class="{
                      'pt-1': isDatetime,
                    }"
                    v-bind="valueInputConfig"
                    :height="20"
                    item-value="value"
                    v-model="valueInputValue"
                    :rules="rules"
                    @input="emit"
                    :disabled="!attributeValueType"
                    :loading="isValueInputLoading"
                  />
                </span>
              </template>
              <span>
                {{ valueTooltip }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { get, call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';

export default {
  name: 'Expression',
  components: {
    DataInput,
  },
  props: {
    dataSourceAttributes: {
      type: Array,
      required: true,
    },
    dataSourceAttributesRelational: {
      type: Array,
      required: true,
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      default: null,
    },
  },
  data: vm => ({
    dataType: {
      name: 'select',
    },
    expression: null,
    mainAttribute: null,
    attributeValue: null,
    attributeValueAttribute: null,
    attributeValueType: null,
    rules: [v => !!v || v === false || v === 0 || vm.$i18n.t('rules.required')],
    relationalAttributeValues: [],
    isValueInputLoading: false,
    isExpressionSpecial: false,
  }),
  computed: {
    dataSources: get('layers/metadata'),
    expressions: get('layers/expressions'),
    valueInputConfig() {
      return this.attributeValueType === 'attribute'
        ? {
            itemText: 'verbose_name',
            items: this.computedRelationalAttributes,
            label: this.chooseAttributeLabel,
            dataType: this.dataType,
          }
        : {
            itemText: 'display_value',
            items: this.selectItems,
            label: this.chooseValueLabel,
            dataType: this.selectedAttributeDataType,
          };
    },
    valueInputValue: {
      get() {
        return this.attributeValueType === 'attribute' ? this.attributeValueAttribute : this.attributeValue;
      },
      set(nV) {
        this.attributeValueType === 'attribute' ? (this.attributeValueAttribute = nV) : (this.attributeValue = nV);
      },
    },
    isDataSourceUseAttributesQualifiedNames() {
      return this.dataSources[this.dataSourceName]?.attributes_use_datasource_qualified_names;
    },
    computedAttributes() {
      return this.dataSourceAttributes.filter(attribute => attribute.data_type.name !== 'geometric');
    },
    computedRelationalAttributes() {
      return this.dataSourceAttributesRelational.filter(
        attribute => attribute.data_type.name === this.selectedAttributeDataType.name
      );
    },
    computedExpressions() {
      return this.expressions?.filter(
        expression => expression.type === 'operator' && expression.allowed_rhs_data_types?.data_type?.name !== 'list'
      );
    },
    selectedAttribute() {
      return this.dataSourceAttributes.find(att => att.name === this.mainAttribute);
    },
    isCombobox() {
      return (
        this.selectedAttribute?.type === 'dict' &&
        (this.expression === '$LIKE' || this.expression === '$LIKE_CASE_INSENSITIVE')
      );
    },
    isSelect() {
      return this.selectedAttribute?.type === 'dict' || this.selectedAttribute?.type === 'relation';
    },
    selectedAttributeDataType() {
      if (this.isCombobox) {
        return {
          name: 'combobox',
        };
      }
      if (this.isSelect) {
        return this.dataType;
      }
      return (
        this.selectedAttribute?.data_type || {
          name: 'text',
        }
      );
    },
    selectItems() {
      if (this.selectedAttribute?.relation) {
        return this.relationalAttributeValues;
      }
      return this.selectedAttribute?.allowed_values;
    },
    isDatetime() {
      return this.selectedAttributeDataType?.name === 'datetime';
    },
    attributeRequiredTooltip() {
      return this.$i18n.t('tooltips.attrbuteRequired');
    },
    valueTypeRequiredTooltip() {
      return this.$i18n.t('tooltips.valueTypeRequired');
    },
    valueTooltip() {
      return !this.mainAttribute ? this.attributeRequiredTooltip : this.valueTypeRequiredTooltip;
    },
    isAttributeRequiredTooltipDisabled() {
      return !!this.mainAttribute;
    },
    isValueTooltipDisabled() {
      return !!this.mainAttribute && !!this.attributeValueType;
    },
    chooseAttributeLabel() {
      return this.$i18n.t('dialog.body.attribute');
    },
    chooseValueLabel() {
      return this.$i18n.t('dialog.body.value');
    },
    chooseExpressionLabel() {
      return this.$i18n.t('dialog.body.expression');
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(nV) {
        this.readValue(nV);
      },
    },
    selectedAttribute(nV) {
      this.onSelectedAttributeChange(nV);
    },
  },
  methods: {
    getRelationValuesMapping: call('layers/getRelationValuesMapping'),
    async onSelectedAttributeChange(nV) {
      this.relationalAttributeValues = [];
      if (nV?.relation) {
        try {
          this.isValueInputLoading = true;
          const {
            datasource: { name },
            name: attribute_name,
          } = nV;
          const r = await this.getRelationValuesMapping({
            data_source_name: name,
            attribute_name,
          });
          this.relationalAttributeValues = r;
        } catch (error) {
          console.log('~ error', error);
        }
        this.isValueInputLoading = false;
      }
    },
    emit(changed = '') {
      if (changed === 'mainAttribute') {
        this.attributeValueType = 'value';
        this.attributeValue = null;
        this.expression = this.expressions[0].name;
        this.expressions.find(expression => expression.name === this.expression).isSpecial
          ? (this.isExpressionSpecial = true)
          : (this.isExpressionSpecial = false);
        return;
      }
      if (this.expression === null) {
        return;
      }
      const value = {};
      const obj = {};

      const name = this.isDataSourceUseAttributesQualifiedNames
        ? this.mainAttribute
        : `${this.dataSourceName}.${this.mainAttribute}`;
      if (this.expressions.find(expression => expression.name === this.expression).isSpecial) {
        this.expressions.find(expression => expression.name === this.expression).negationName
          ? (value['NOT'] = {
              [this.expressions.find(expression => expression.name === this.expression).negationName]: name,
            })
          : (value[this.expression] = name);
      } else {
        obj[name] = {
          [this.attributeValueType]:
            this.attributeValueType === 'value' ? this.attributeValue : this.attributeValueAttribute,
        };
        value[this.expression] = obj;
      }
      this.$emit('input', value);
    },
    readValue(nV) {
      if (!this.mainAttribute) {
        this.isExpressionSpecial = false;
      }
      if (!nV) {
        return;
      }
      const keyName = Object.keys(nV)[0] === 'NOT' ? Object.keys(nV['NOT'])[0] : Object.keys(nV)[0];
      const lhsName = this.expressions.find(expression => expression.name === keyName).isSpecial
        ? Object.keys(nV)[0] === 'NOT'
          ? nV['NOT'][keyName]
          : nV[keyName]
        : Object.keys(nV[keyName])[0];
      this.expressions.find(expression => expression.name === keyName).isSpecial
        ? (this.isExpressionSpecial = true)
        : (this.isExpressionSpecial = false);
      const attributeName = this.isDataSourceUseAttributesQualifiedNames ? lhsName : lhsName.split('.')[1];
      this.mainAttribute = attributeName;
      this.expression =
        Object.keys(nV)[0] === 'NOT'
          ? this.expressions.find(expression => expression.negationName === keyName).name
          : keyName;
      const value = nV[keyName] ? nV[keyName][lhsName] : null;
      if (!value) {
        this.attributeValueType = null;
        this.attributeValue = null;
        return;
      }
      const [attributeValueType] = Object.keys(value);

      this.attributeValueType = attributeValueType;
      if (attributeValueType === 'attribute') {
        this.attributeValueAttribute = value[attributeValueType];
      } else {
        this.attributeValue = value[attributeValueType];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-list-item__title {
    @include selectListElement();
  }
  .v-input {
    font-size: 14px;
  }
  .v-input__slot {
    margin-bottom: 2px !important;
  }
  .v-text-field:not(.v-select) .v-input__append-inner {
    margin-top: -1px !important;
  }
  .input--type {
    .v-icon {
      font-size: 15px;
    }
    .v-label {
      font-size: 12px;
    }
    .v-input--selection-controls__ripple {
      width: 15px;
      height: 15px;
      left: -3px;
      top: calc(50% - 15px) !important;
    }
    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }
  .vue-input {
    font-size: 13px;
  }
}
</style>
